// Thin fetch wrapper. All calls go through here so the JWT lives in
// exactly one place (localStorage under TOKEN_KEY) and a 401 logs the
// user out from one spot. Anything more ambitious (react-query, swr,
// rtk-query) is deferred until we have more than two endpoints.

const TOKEN_KEY = "customer-app:token";
const SITE_KEY = "customer-app:site";
const NAME_KEY = "customer-app:name";
// Gekozen installatie bij een login met meerdere sites.
const ACTIVE_SITE_KEY = "customer-app:active-site";

export interface LoginResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  site_id: string;
  display_name: string;
}

export interface EvLive {
  /** Hangt de auto aan de paal? Zo niet, dan verbergt het stroomschema
   *  de auto helemaal - evcc bevriest zijn auto-gegevens bij afkoppelen,
   *  dus alles wat we dan zouden tonen is oude informatie. */
  connected: boolean;
  charging: boolean;
  charge_power_w: number;
  vehicle_soc_pct: number | null;
  vehicle: string | null;
}

export interface Live {
  pv_w: number;
  battery_w: number;
  grid_w: number;
  load_w: number;
  soc: number;
  timestamp: string;
  /** Present only when the site has evcc configured AND it answered. */
  ev?: EvLive | null;
}

export interface TodayExpected {
  pv_kwh: number | null;
  load_kwh: number | null;
  grid_in_kwh: number | null;
  grid_out_kwh: number | null;
  to_battery_kwh: number | null;
  from_battery_kwh: number | null;
}

export interface Today {
  pv_kwh: number;
  to_battery_kwh: number;
  from_battery_kwh: number;
  grid_in_kwh: number;
  grid_out_kwh: number;
  load_kwh: number;
  self_sufficiency_pct: number;
  date: string;
  /** Where today is expected to end (measured + forecast/plan for the
   *  rest of the day); null fields = unknown, null block = nothing known. */
  expected?: TodayExpected | null;
}

export type LiveRange = "15m" | "1h" | "6h" | "24h";

export interface LiveSeries {
  range: LiveRange;
  step_seconds: number;
  t: number[];          // unix seconds
  pv_w: number[];
  battery_w: number[];
  grid_w: number[];
  load_w: number[];
  /** EV wallbox draw - null/absent for sites without evcc. */
  ev_w?: number[] | null;
  /** Auto-SOC per ~15 min; null-gaten = auto los / meting gemist. */
  ev_soc_pct?: (number | null)[] | null;
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function getSession(): { site_id: string; name: string } | null {
  const t = getToken();
  if (!t) return null;
  return {
    site_id: localStorage.getItem(SITE_KEY) ?? "",
    name: localStorage.getItem(NAME_KEY) ?? "",
  };
}

export function clearSession(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(SITE_KEY);
  localStorage.removeItem(NAME_KEY);
  localStorage.removeItem(ACTIVE_SITE_KEY);
}

// --- actieve site (alleen relevant bij meerdere installaties) ---------
//
// De KEUZE staat hier; de TOEGANG staat in het token. Vraagt de app om
// een site die niet in het token zit, dan antwoordt de backend 403 -
// deze waarde kan dus nooit meer rechten opleveren dan de login heeft.

export interface SiteChoice {
  id: string;
  name: string;
  is_default: boolean;
}

export function getActiveSite(): string {
  return localStorage.getItem(ACTIVE_SITE_KEY) ?? "";
}

export function setActiveSite(siteId: string): void {
  if (siteId) localStorage.setItem(ACTIVE_SITE_KEY, siteId);
  else localStorage.removeItem(ACTIVE_SITE_KEY);
}

export async function fetchMySites(): Promise<SiteChoice[]> {
  const r = await authedFetch("/api/sites");
  if (!r.ok) return [];
  const body = (await r.json()) as { sites: SiteChoice[] };
  return body.sites ?? [];
}

// In de Capacitor-app (native WebView, https://localhost-origin) zijn
// relatieve /api-paden zinloos - daar praat de app rechtstreeks met de
// publieke backend. Op het web blijft alles relatief (zelfde origin,
// nginx proxyt /api). VITE_API_BASE laat een testbuild naar een andere
// backend wijzen zonder codewijziging.
export const API_BASE: string =
  (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } })
    .Capacitor?.isNativePlatform?.()
    ? (import.meta.env.VITE_API_BASE ?? "https://app.mijnslimstroom.nl")
    : "";

/** Versie-headers: laten de backend zien of dit de native app of het
 *  web is, en welke OTA-bundel draait. Voedt het gebruiksdashboard
 *  (adoptie van nieuwe bundels na een deploy). Alleen native. */
let appVersions: { native: string; bundle: string } | null = null;
export function setAppVersions(native: string, bundle: string): void {
  appVersions = { native, bundle };
}
function versionHeaders(): Record<string, string> {
  if (!appVersions) return {};
  return {
    "X-App-Native": appVersions.native,
    "X-App-Bundle": appVersions.bundle,
  };
}

async function authedFetch(path: string): Promise<Response> {
  const token = getToken();
  const r = await fetch(API_BASE + withSite(path), {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...versionHeaders(),
    },
  });
  if (r.status === 401) {
    // Token expired or invalidated server-side - drop it and reload
    // so the router takes the user back to the login screen.
    clearSession();
    window.location.assign("/login");
  }
  return r;
}

/** Plakt de gekozen site aan elk verzoek. Eén plek, zodat een nieuw
 * endpoint niet per ongeluk de standaardsite blijft tonen terwijl de
 * gebruiker net omgeschakeld heeft. Geen keuze = geen parameter, dus
 * gewone klanten sturen exact wat ze altijd stuurden. */
function withSite(path: string): string {
  const site = getActiveSite();
  if (!site || path.startsWith("/api/auth/") || path.startsWith("/api/sites"))
    return path;
  return path + (path.includes("?") ? "&" : "?") + "site=" + encodeURIComponent(site);
}

/** Mutaties (PUT/DELETE) liepen langs authedFetch heen: ze misten
 * API_BASE (stuk in de native app, waar relatieve /api-paden nergens
 * heen wijzen) en zouden na omschakelen naar de STANDAARDsite
 * schrijven in plaats van de bekeken site. Alles nu via één pad. */
async function authedMutate(
  path: string,
  method: "PUT" | "POST" | "DELETE",
  body?: unknown,
): Promise<Response> {
  const token = getToken();
  const r = await fetch(API_BASE + withSite(path), {
    method,
    headers: {
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...versionHeaders(),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (r.status === 401) {
    clearSession();
    window.location.assign("/login");
    throw new Error("session expired");
  }
  return r;
}

export async function login(
  username: string,
  password: string,
): Promise<LoginResponse> {
  const r = await fetch(API_BASE + "/api/auth/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
  if (r.status === 429) {
    // Login jail (backend customer_app/jail.py): too many failures on
    // this account or from this address; Retry-After carries seconds.
    const wait = Number(r.headers.get("Retry-After") ?? "0");
    const mins = Math.max(1, Math.ceil(wait / 60));
    throw new Error(
      `Te veel mislukte inlogpogingen. Probeer het over ${mins} ${mins === 1 ? "minuut" : "minuten"} opnieuw.`,
    );
  }
  if (!r.ok) {
    // The backend returns the same 401 for unknown user and wrong
    // password, so we don't try to differentiate here.
    throw new Error("Onjuiste gebruikersnaam of wachtwoord");
  }
  const data: LoginResponse = await r.json();
  localStorage.setItem(TOKEN_KEY, data.access_token);
  localStorage.setItem(SITE_KEY, data.site_id);
  localStorage.setItem(NAME_KEY, data.display_name);
  return data;
}

// --- rolling session --------------------------------------------------
// The backend token lives 90 days; swapping it for a fresh one whenever
// it is older than a day makes that a ROLLING 90 days - only a customer
// who does not open the app for three months sees the login screen
// again. (It was a fixed 7 days without renewal: everyone was logged
// out a week after logging in, however often they used the app.)
const REFRESH_AFTER_SEC = 24 * 60 * 60;
let refreshing = false;

function tokenIssuedAt(token: string): number | null {
  try {
    const part = token.split(".")[1];
    const json = atob(part.replace(/-/g, "+").replace(/_/g, "/"));
    const iat = JSON.parse(json).iat;
    return typeof iat === "number" ? iat : null;
  } catch {
    return null;
  }
}

/** Call at boot and whenever the app comes back to the foreground.
 * Never throws and never logs out by itself: offline or a 5xx just
 * leaves the current token in place (a 401 is handled by authedFetch
 * callers' usual path - here it means the account is gone). */
export async function maybeRefreshSession(): Promise<void> {
  const token = getToken();
  if (!token || refreshing) return;
  const iat = tokenIssuedAt(token);
  if (iat !== null && Date.now() / 1000 - iat < REFRESH_AFTER_SEC) return;
  refreshing = true;
  try {
    const r = await fetch(API_BASE + "/api/auth/refresh", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, ...versionHeaders() },
    });
    if (r.status === 401) {
      clearSession();
      window.location.assign("/login");
      return;
    }
    if (!r.ok) return;
    const data: LoginResponse = await r.json();
    // The user may have logged out (or in as someone else) meanwhile.
    if (getToken() !== token) return;
    localStorage.setItem(TOKEN_KEY, data.access_token);
    localStorage.setItem(SITE_KEY, data.site_id);
    localStorage.setItem(NAME_KEY, data.display_name);
  } catch {
    // offline: keep the token we have
  } finally {
    refreshing = false;
  }
}

export async function fetchLive(): Promise<Live> {
  const r = await authedFetch("/api/live");
  if (!r.ok) throw new Error(`live: ${r.status}`);
  return r.json();
}

export async function fetchToday(): Promise<Today> {
  const r = await authedFetch("/api/today");
  if (!r.ok) throw new Error(`today: ${r.status}`);
  return r.json();
}

export async function fetchLiveSeries(range: LiveRange): Promise<LiveSeries> {
  const r = await authedFetch(`/api/live/series?range=${range}`);
  if (!r.ok) throw new Error(`live/series: ${r.status}`);
  return r.json();
}

// Edge-device liveness for the "Apparaatstatus" home card. Mirrors
// customer_app/models.py > DeviceStatusResponse. `state` separates
// "enrolled" from "healthy": `pending` is the soft amber state (no
// fresh data yet, or the battery isn't reporting) - NOT an error.
export type DeviceState = "online" | "pending" | "offline";

export interface DeviceStatus {
  state: DeviceState;
  since: string | null;   // ISO datetime UTC; meaning given by since_kind
  // "uptime" = real boot time of the edge device, "streak" = start of
  // the uninterrupted telemetry streak (no edge device), "last_seen" =
  // last contact (pending/offline).
  since_kind: "uptime" | "streak" | "last_seen" | null;
  since_capped: boolean;  // streak ran into the 30-day window
  battery_present: boolean;
  edge_device: boolean;   // a live SBC heartbeats for this site
}

export async function fetchDeviceStatus(): Promise<DeviceStatus> {
  const r = await authedFetch("/api/device/status");
  if (!r.ok) throw new Error(`device/status: ${r.status}`);
  return r.json();
}

export interface PricingToday {
  date: string;                          // ISO yyyy-mm-dd in customer-local time
  t: number[];                           // unix seconds at the START of each bucket
  prices_eur_per_kwh: (number | null)[]; // null for buckets with no sample yet
  current_hour_idx: number;              // index into t / prices - "now"
  resolution: "1h" | "15min";            // echoes the backend choice
  days?: number;                         // window from local midnight (2..14)
  forecast_from?: number | null;         // buckets from this ts are AI forecast
  // What this site RECEIVES per exported kWh (its contract), aligned
  // with t; null when the site has no pricing config.
  export_prices_eur_per_kwh?: (number | null)[] | null;
  export_mode?: "flat" | "saldering" | "dynamic" | null;
}

/** Default "1h" keeps the Prijzen chart matching what Zonneplan/Frank/
 *  GBB display. Pass "15min" for Tibber-style quarter-hour bars (reads
 *  the `nl_dayahead_price_eur_per_kwh_15min` series entsoe-poller
 *  writes alongside the hourly mean). The toggle lives in
 *  Account → Energiecontract → "Tijdsblokken van 15 minuten". */
export async function fetchPricingToday(
  resolution: "1h" | "15min" = "1h",
  days = 2,
): Promise<PricingToday> {
  const params = new URLSearchParams();
  if (resolution === "15min") params.set("resolution", "15min");
  if (days > 2) params.set("days", String(days));
  const qs = params.toString() ? `?${params}` : "";
  const r = await authedFetch(`/api/pricing/today${qs}`);
  if (!r.ok) throw new Error(`pricing/today: ${r.status}`);
  return r.json();
}

export interface PlanHistory {
  date: string;                          // YYYY-MM-DD (Amsterdamse dag)
  t: number[];                           // kwartier-starts, unix seconden
  planned_soc_pct: (number | null)[];    // ems_plan_soc_pct zoals opgeslagen
  actual_soc_pct: (number | null)[];     // gemeten SOC (merk-generieke unie)
  prices_eur_per_kwh: (number | null)[];
}

export async function fetchPlanHistory(day: string): Promise<PlanHistory> {
  const r = await authedFetch(`/api/plan/history?day=${day}`);
  if (!r.ok) throw new Error(`plan/history: ${r.status}`);
  return r.json();
}

// Matches EARNINGS_RANGE_KEYS in customer_app/queries.py. `custom`
// requires `start` + `end` (unix seconds) on the wire; the others
// compute their own window server-side relative to now.
export type EarningsRange =
  | "today"
  | "yesterday"
  | "last_7d"
  | "last_30d"
  | "month"
  | "prev_month"
  | "year"
  | "prev_year"
  | "custom";

export interface EarningsParams {
  range: EarningsRange;
  /** Required when range = "custom". Unix seconds. */
  start?: number;
  end?: number;
  /** Override the inferred bucket on /api/earnings/series only. */
  bucket?: "hour" | "day" | "month";
}

export interface Earnings {
  range: EarningsRange;
  start: string;            // ISO datetime UTC
  end: string;              // ISO datetime UTC
  eur_spent: number;        // import cost
  eur_earned: number;       // export gain
  eur_net: number;          // earned - spent (negative = net cost)
  contract: "dynamic" | "fixed" | null;
}

function earningsQs(p: EarningsParams): string {
  const u = new URLSearchParams({ range: p.range });
  if (p.range === "custom") {
    if (p.start != null) u.set("start", String(p.start));
    if (p.end != null) u.set("end", String(p.end));
  }
  if (p.bucket) u.set("bucket", p.bucket);
  return u.toString();
}

export interface Savings {
  range: string;
  start_utc: string;
  battery_eur: number;
  pv_eur: number;
  total_eur: number;
  battery_shifted_kwh: number;
  pv_generated_kwh: number;
  contract: string | null;
  /** Uitsplitsing van battery_eur: avoided - grid_charge - unsold_pv. */
  battery_avoided_import_eur?: number;
  battery_grid_charge_eur?: number;
  battery_unsold_pv_eur?: number;
  battery_discharged_kwh?: number;
  battery_grid_charged_kwh?: number;
  battery_pv_charged_kwh?: number;
}

/** Besparing door accu + zon - de terugverdien-teller. */
export async function fetchSavings(range: "today" | "month" | "year" | "all"): Promise<Savings> {
  const r = await authedFetch(`/api/savings?range=${range}`);
  if (!r.ok) throw new Error(`savings: ${r.status}`);
  return r.json();
}

export async function fetchEarnings(p: EarningsParams): Promise<Earnings> {
  const r = await authedFetch(`/api/earnings?${earningsQs(p)}`);
  if (!r.ok) throw new Error(`earnings: ${r.status}`);
  return r.json();
}

export interface EarningsSeries {
  range: EarningsRange;
  bucket: "hour" | "day" | "month";
  start: string;
  end: string;
  t: number[];                  // bucket-start unix seconds, ascending
  eur_earned: number[];
  eur_spent: number[];
  eur_net: number[];
  contract: "dynamic" | "fixed" | null;
}

export async function fetchEarningsSeries(
  p: EarningsParams,
): Promise<EarningsSeries> {
  const r = await authedFetch(`/api/earnings/series?${earningsQs(p)}`);
  if (!r.ok) throw new Error(`earnings/series: ${r.status}`);
  return r.json();
}

export interface HistorySeries {
  range: EarningsRange;                  // reuses the earnings range vocab
  bucket: "hour" | "day" | "month";
  start: string;                         // ISO datetime UTC
  end: string;
  t: number[];                           // bucket-start unix seconds
  pv_kwh: number[];
  load_kwh: number[];                    // derived from conservation
  to_battery_kwh: number[];              // charging
  from_battery_kwh: number[];            // discharging
  grid_in_kwh: number[];                 // imported
  grid_out_kwh: number[];                // exported
  /** Bucket-avg wholesale EPEX (€/kWh). `null` when no entsoe sample
   *  covered that bucket. Apply contract math on top for the all-in
   *  €/kWh the customer actually pays. */
  price_epex_eur_per_kwh: (number | null)[];
  /** Per UUR berekend over de range: deel van het huisverbruik dat
   *  niet op dat moment van het net kwam. De naieve range-formule
   *  klapt naar 0% op arbitrage-accu's (import >> verbruik). */
  self_sufficiency_pct: number | null;
}

/** Reuses `EarningsParams` for the wire (same range + custom-window
 *  shape; the backend just ignores the EUR-only fields). One endpoint
 *  per concept keeps the contract clean - no `kind=earnings|energy`
 *  query-param polymorphism. */
export async function fetchHistorySeries(
  p: EarningsParams,
): Promise<HistorySeries> {
  const r = await authedFetch(`/api/history/series?${earningsQs(p)}`);
  if (!r.ok) throw new Error(`history/series: ${r.status}`);
  return r.json();
}

export type PricingContract = "dynamic" | "fixed";

export type ExportMode = "flat" | "saldering" | "dynamic";

export interface PricingConfigBody {
  contract: PricingContract;
  fixed_eur_per_kwh: number | null;
  // Import - EXCL BTW. Dynamic = (epex + margin + tax) × (1 + vat_pct).
  supplier_margin_eur_per_kwh: number;
  tax_eur_per_kwh: number;
  vat_pct: number;
  // Export
  export_mode: ExportMode;
  // flat-mode only:
  feed_in_tariff_eur_per_kwh: number;
  // dynamic-mode only:
  sun_bonus_pct: number;                  // 0.10 = Zonneplan zonnebonus
  export_margin_eur_per_kwh: number;      // negative = Tibber-style sales fee
}

export interface PricingConfigResponse {
  source: "override" | "default" | "none";
  effective: PricingConfigBody | null;
}

export async function fetchPricingConfig(): Promise<PricingConfigResponse> {
  const r = await authedFetch("/api/pricing/config");
  if (!r.ok) throw new Error(`pricing/config: ${r.status}`);
  return r.json();
}

export async function savePricingConfig(
  body: PricingConfigBody,
): Promise<PricingConfigResponse> {
  const r = await authedMutate("/api/pricing/config", "PUT", body);
  if (!r.ok) {
    const text = await r.text().catch(() => "");
    throw new Error(`pricing/config save: ${r.status} ${text}`);
  }
  return r.json();
}

export interface EvccConfigBody {
  enabled: boolean;
  url: string;
  behind_meter: boolean;
}
export interface EvccConfigResponse extends EvccConfigBody {
  source: "override" | "default";
}

export async function fetchEvccConfig(): Promise<EvccConfigResponse> {
  const r = await authedFetch("/api/evcc/config");
  if (!r.ok) throw new Error(`evcc/config: ${r.status}`);
  return r.json();
}

export async function saveEvccConfig(
  body: EvccConfigBody,
): Promise<EvccConfigResponse> {
  const r = await authedMutate("/api/evcc/config", "PUT", body);
  if (!r.ok) {
    const text = await r.text().catch(() => "");
    throw new Error(`evcc/config save: ${r.status} ${text}`);
  }
  return r.json();
}

/** The site's evcc tariff feed URLs (user-defined tariff, forecast plugin). */
export interface EvccTariffUrls {
  grid_url: string;
  feedin_url: string;
  resolution: string;
}

export async function fetchEvccTariffUrls(): Promise<EvccTariffUrls> {
  const r = await authedFetch("/api/evcc/tariff-urls");
  if (!r.ok) throw new Error(`evcc/tariff-urls: ${r.status}`);
  return r.json();
}

export async function resetEvccConfig(): Promise<EvccConfigResponse> {
  const r = await authedMutate("/api/evcc/config", "DELETE");
  if (!r.ok) throw new Error(`evcc/config reset: ${r.status}`);
  return r.json();
}

export interface ReserveConfigResponse {
  source: "override" | "default";
  min_soc_pct: number;          // effective floor the plan keeps
  default_min_soc_pct: number;  // installer's floor (lower bound)
  max_pct: number;              // upper bound the engine accepts
}

export async function fetchReserveConfig(): Promise<ReserveConfigResponse> {
  const r = await authedFetch("/api/reserve/config");
  if (!r.ok) throw new Error(`reserve/config: ${r.status}`);
  return r.json();
}

export async function saveReserveConfig(min_soc_pct: number): Promise<ReserveConfigResponse> {
  const r = await authedMutate("/api/reserve/config", "PUT", { min_soc_pct });
  if (!r.ok) {
    const text = await r.text().catch(() => "");
    throw new Error(`reserve/config save: ${r.status} ${text}`);
  }
  return r.json();
}

export async function resetReserveConfig(): Promise<ReserveConfigResponse> {
  const r = await authedMutate("/api/reserve/config", "DELETE");
  if (!r.ok) throw new Error(`reserve/config reset: ${r.status}`);
  return r.json();
}

export async function resetPricingConfig(): Promise<PricingConfigResponse> {
  const r = await authedMutate("/api/pricing/config", "DELETE");
  if (!r.ok) throw new Error(`pricing/config reset: ${r.status}`);
  return r.json();
}

// --- Dynamic ESS forecast (Prognose tab) -----------------------------------

export interface ForecastSlot {
  start: string;                        // ISO datetime UTC (slot begin)
  duration_sec: number;
  target_soc_pct: number;               // the PLAN: SOC the system steers toward
  soc_limit_pct: number | null;         // raw Venus Soc field (bound, not plan)
  strategy: number | null;              // Victron enum, raw
  allow_grid_feed_in: boolean | null;
  price_eur_per_kwh: number | null;
  action: "laden" | "ontladen" | "rust";
}

export interface EnergyForecast {
  t: number[];                          // bucket starts, unix seconds
  kwh: (number | null)[];
}

export interface EmsTrajectoryPoint {
  t: number; // unix seconds
  soc_pct: number;
}

export interface EmsAction {
  start: string; // ISO datetime
  end: string;
  action: "laden" | "ontladen" | "rust";
  avg_power_w: number;
  energy_kwh: number;
  reason: string;
}

export interface EmsPricePoint {
  t: number; // slot start, unix seconds
  epex_eur_per_kwh: number; // raw epex - apply the all-in transform client-side
  /** Sell price in that slot (engine since 2026-10-04); absent on older plans. */
  export_price_eur_per_kwh?: number | null;
}

/** Slim Stroom's own (monitor-only) plan, shown next to the DESS plan.
 *  Null when the engine is down or the site isn't EMS-enabled - the tab
 *  then renders exactly as before this feature existed. */
export interface EmsLoadPoint {
  t: number;
  house_w: number;
  ev_w: number;
  pv_w: number;
  /** planned grid power, +import / -export (W); null on older plans */
  grid_w: number | null;
  /** planned battery power, +charge / -discharge (W); null on older plans */
  battery_w: number | null;
}

/** Battery balancing round (engine balancing.py): every interval_days
 *  the pack sits at >= soc_pct for hold_hours; window_* set when the
 *  current plan holds one, last_balanced_at is measured. */
export interface EmsBalancing {
  due: boolean;
  last_balanced_at: number | null;
  window_start: number | null;
  window_end: number | null;
  soc_pct: number;
  hold_hours: number;
  interval_days: number;
}

export interface EmsPlan {
  trajectory: EmsTrajectoryPoint[];
  actions: EmsAction[];
  balancing?: EmsBalancing | null;
  projected_ems_eur: number | null;
  /** Kosten ZONDER sturing over dezelfde horizon - de merkonafhankelijke
   *  vergelijking. Verving projected_dess_eur toen de schaduwproef
   *  eindigde (2026-08-14): niet elke klant heeft Victron. */
  projected_baseline_eur: number | null;
  window_end: number | null;
  created_at: number | null;
  /** First ts where prices are AI forecast (energyforecast.de), not known day-ahead. */
  known_price_until: number | null;
  /** Forecast-region price slots (ts >= known_price_until). */
  price_forecast: EmsPricePoint[];
  /** Forecast components per 15-min slot (W): house incl. warmtepomp,
   *  EV charging (evcc plan), PV. Empty on pre-decomposition plans. */
  loads: EmsLoadPoint[];
}

export interface ForecastResponse {
  available: boolean;
  slots: ForecastSlot[];
  solar_forecast: EnergyForecast | null;
  consumption_forecast: EnergyForecast | null;
  ems_plan: EmsPlan | null;
}

export async function fetchForecast(hours: 24 | 48 | 72 = 24): Promise<ForecastResponse> {
  const r = await authedFetch(`/api/forecast?hours=${hours}`);
  if (!r.ok) throw new Error(`forecast: ${r.status}`);
  return r.json();
}

// ---- Slimme apparaten (Account > Slimme apparaten) ----------------------
export type SmartKind = "hot_water" | "dishwasher" | "washer" | "dryer" | "pump" | "generic";
export type SmartMode = "window" | "deadline" | "daily_hours";
export interface SmartLoad {
  id: string;
  name: string;
  kind: SmartKind;
  power_kw: number;
  duration_min: number;
  mode: SmartMode;
  window_start: string;   // HH:MM
  window_end: string;
  deadline: string;
  lead_hours: number;
  daily_hours: number;
  prefer_surplus: boolean;
  enabled: boolean;
  calibrate?: boolean;     // measured runs replace power/duration (default on)
}
export interface SmartConfigBody {
  enabled: boolean;
  heating_enabled: boolean;
  loads: SmartLoad[];
}
export interface SmartConfigResponse extends SmartConfigBody {
  source: "override" | "default";
  kinds: string[];
  modes: string[];
}
export interface SmartWindow { start: number; end: number }
export interface SmartLoadAdvice {
  id: string; name: string; kind: SmartKind; mode: SmartMode;
  available: boolean; active_now: boolean; windows: SmartWindow[]; forecast: boolean;
  cost_eur?: number | null; cost_now_eur?: number | null; worst_eur?: number | null;
  saving_eur?: number | null; reason?: string | null; deadline?: number | null;
  calibrated?: boolean; effective_power_kw?: number | null; effective_duration_min?: number | null;
  stats?: SmartLoadStats | null;
}
/** One reported run (Home Assistant or by hand), scored by the engine. */
export interface SmartRun {
  load_id: string; started_at: number; ended_at: number | null; kwh: number | null; source: string;
  reported_at: number; kwh_used: number | null; in_window: boolean | null;
  cost_eur: number | null; worst_eur: number | null; saving_eur: number | null;
}
export interface SmartLoadStats {
  days: number; runs: number; measured: number; scored: number; followed: number;
  followed_pct: number | null; saving_eur: number; kwh: number;
  last_run: SmartRun | null; running_since: number | null;
}
export interface SmartRunsResponse { runs: SmartRun[]; stats: Record<string, SmartLoadStats> }
export interface SmartResponse {
  enabled: boolean;
  available: boolean;
  generated_at?: number | null;
  known_until?: number | null;
  price_tier_now?: "goedkoop" | "normaal" | "duur" | null;
  cost_now_eur_per_kwh?: number | null;
  best_2h?: { start: number; end: number; eur_per_kwh: number; forecast: boolean } | null;
  negative_price?: { now: boolean; next_start: number | null } | null;
  heating?: { advice: "bijstoken" | "normaal" | "temperen"; until: number | null; next: string | null } | null;
  loads: SmartLoadAdvice[];
}
export async function fetchSmartConfig(): Promise<SmartConfigResponse> {
  const r = await authedFetch("/api/smart/config");
  if (!r.ok) throw new Error(`smart/config: ${r.status}`);
  return r.json();
}
export async function saveSmartConfig(body: SmartConfigBody): Promise<SmartConfigResponse> {
  const r = await authedMutate("/api/smart/config", "PUT", body);
  if (!r.ok) {
    const text = await r.text().catch(() => "");
    throw new Error(`smart/config save: ${r.status} ${text}`);
  }
  return r.json();
}
export async function resetSmartConfig(): Promise<SmartConfigResponse> {
  const r = await authedMutate("/api/smart/config", "DELETE");
  if (!r.ok) throw new Error(`smart/config reset: ${r.status}`);
  return r.json();
}
export async function fetchSmart(): Promise<SmartResponse> {
  const r = await authedFetch("/api/smart");
  if (!r.ok) throw new Error(`smart: ${r.status}`);
  return r.json();
}
export async function fetchSmartRuns(limit = 50): Promise<SmartRunsResponse> {
  const r = await authedFetch(`/api/smart/runs?limit=${limit}`);
  if (!r.ok) throw new Error(`smart/runs: ${r.status}`);
  return r.json();
}
export async function clearSmartRuns(): Promise<SmartRunsResponse> {
  const r = await authedMutate("/api/smart/runs", "DELETE");
  if (!r.ok) throw new Error(`smart/runs clear: ${r.status}`);
  return r.json();
}
